import { DateTime } from "luxon";

import { normalizeDateTime, type DateTimeLike } from './date-time';
import { MARKET_TZ, type MarketInstant, toMarketTime } from './market-clock';

/*
 * One rule for where a webinar session stands, shared by every surface that shows one. The dashboard, the featured
 * replay, the listing tabs and the preview badge each had their own (start > now − 1h, start ≤ now − 1h, start ≥ now,
 * start of day ≥ local start of day), so a live session was 'Past' on one card and 'Upcoming' on the next.
 * Pure (no Vue): apps/web reads it through the shared market clock, so the phase moves on in an open tab.
 */

/** 'upcoming' before the start, 'live' from the start until the session's length has run, then 'past'. */
export type WebinarPhase = 'upcoming' | 'live' | 'past';

/** The CMS carries no session length; sessions run about an hour, and the dashboard always kept one listed that long. */
export const WEBINAR_DEFAULT_DURATION_MIN: number = 60;

/**
 * The session's start, or null when there is none to read. normalizeDateTime( undefined ) is "now", so an empty
 * value is caught here rather than read as a session starting this instant.
 */
export function webinarStartTime( start: DateTimeLike | null ): DateTime | null {
    if ( null == start || '' === start ) {
        return null;
    }

    const dt: DateTime | null = normalizeDateTime( start );
    return dt?.isValid ? dt : null;
}

/** A session with no usable start counts as past: legacy content without a date has, in practice, already aired. */
export function webinarPhase( start: DateTimeLike | null, now: MarketInstant, durationMin: number = WEBINAR_DEFAULT_DURATION_MIN ): WebinarPhase {
    const startsAt: DateTime | null = webinarStartTime( start );
    if ( !startsAt ) {
        return 'past';
    }

    const nowMillis: number = toMarketTime( now ).toMillis();
    const startMillis: number = startsAt.toMillis();
    if ( nowMillis < startMillis ) {
        return 'upcoming';
    }

    return nowMillis < startMillis + Math.max( 0, durationMin ) * 60_000 ? 'live' : 'past';
}

export interface WebinarStartLabels {
    /** The start instant, ISO, for a <time datetime>. */
    iso: string;
    /** The start on New York's clock: 'Tue, Sep 29, 2:00 PM ET'. */
    market: string;
    /** The same instant on the viewer's clock ('11:00 AM PDT', weekday added when the day differs); '' on ET. */
    local: string;
    /** The session's New York calendar day, 'September 29, 2026', for a session that has aired. */
    day: string;
}

const LABEL_LOCALE: string = 'en-US';

/**
 * How a session's start reads. Webinars are scheduled on New York time, so that is the time shown; a member whose
 * zone keeps a different offset at that instant also gets their own. The viewer's zone is Luxon's default zone
 * (the browser's, unless Settings.defaultZone says otherwise).
 */
export function webinarStartLabels( start: DateTimeLike | null ): WebinarStartLabels | null {
    const startsAt: DateTime | null = webinarStartTime( start );
    if ( !startsAt ) {
        return null;
    }

    const market: DateTime = startsAt.setZone( MARKET_TZ ).setLocale( LABEL_LOCALE );
    const local: DateTime = startsAt.toLocal().setLocale( LABEL_LOCALE );
    const sameDay: boolean = market.toISODate() === local.toISODate();

    return {
        iso: startsAt.toUTC().toISO() ?? '',
        market: `${ market.toFormat( 'ccc, LLL d, h:mm a' ) } ET`,
        local: local.offset === market.offset ? '' : local.toFormat( sameDay ? 'h:mm a ZZZZ' : 'ccc h:mm a ZZZZ' ),
        day: market.toFormat( 'LLLL d, yyyy' )
    };
}

/** A video's length for a label: '58 min', '1 hr 5 min'. '' for a length that is not a positive number. */
export function webinarLengthLabel( seconds: number | null | undefined ): string {
    if ( null == seconds || !Number.isFinite( seconds ) || seconds <= 0 ) {
        return '';
    }

    const minutes: number = Math.max( 1, Math.round( seconds / 60 ) );
    const hours: number = Math.floor( minutes / 60 );
    const rest: number = minutes % 60;
    if ( 0 === hours ) {
        return `${ minutes } min`;
    }

    return rest > 0 ? `${ hours } hr ${ rest } min` : `${ hours } hr`;
}

/**
 * The registration or join link to open, or null. Only an http(s) URL is used: the value is CMS text bound to an
 * href, and a scheme-less 'zoom.us/…' is common enough to read as https.
 */
export function webinarRegistrationUrl( value: string | null | undefined ): string | null {
    const raw: string = ( value ?? '' ).trim();
    if ( !raw ) {
        return null;
    }

    let url: URL;
    try {
        url = new URL( /^[a-z][a-z\d+.-]*:/i.test( raw ) ? raw : `https://${ raw.replace( /^\/+/, '' ) }` );
    } catch {
        return null;
    }

    return 'https:' === url.protocol || 'http:' === url.protocol ? url.toString() : null;
}
